import {
  ArrowRight,
  Download,
  LockKeyhole,
  Network,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import SectionReveal from "@/components/SectionReveal";
import SEO from "@/components/SEO";
import {
  SECUREAI_CAPABILITIES,
  SECUREAI_CUSTOMER_PROMISE,
  SECUREAI_FAMILY,
  SECUREAI_HERO_STRAPLINE,
  SECUREAI_PLATFORMS,
  SECUREAI_RELEASE,
} from "@/config/secureAiMarketing";
import {
  macosDownloadLabel,
  macosDownloadLive,
  secureAiStorefront,
} from "@/config/secureAiStorefront";

const capabilityIcons = [Network, ShieldCheck, LockKeyhole, Sparkles];
const macos = secureAiStorefront.platforms.macos;
const downloadReady = macosDownloadLive(secureAiStorefront);

const benefits = [
  {
    icon: Network,
    eyebrow: "Protected path",
    title: "Protection on the path out.",
    body: "SecureAI brings Halo-family network protection to compatible AI coding and agent workflows — so development context leaves on a controlled path.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Verified, not assumed",
    title: "A clear protected state.",
    body: "SecureAI only reports Protected after verification on the active path. No false confidence.",
  },
  {
    icon: LockKeyhole,
    eyebrow: "Private by design",
    title: "Your prompts stay yours.",
    body: "SecureAI does not inspect prompts, source code or tokens. It protects the connection — not the content of your work.",
  },
];

const faq = [
  [
    "Is this a Mac App Store download?",
    "SecureAI for Mac is being prepared for direct download from LightningFlow. Public downloads are coming soon.",
  ],
  [
    "Which Macs can run it?",
    `${macos.os}, ${macos.chip}.`,
  ],
  [
    "Is Windows ready?",
    "The release plans shown here cover Mac. Windows availability has not been announced.",
  ],
  [
    "How do I verify the download?",
    "Download details will be published when SecureAI for Mac is available.",
  ],
  [
    "Does SecureAI read my prompts or code?",
    "No. SecureAI is not a content-inspection product. It does not capture prompts, source code or tokens.",
  ],
  [
    "Which AI tools does it support?",
    "Grok Bot and Claude Code support are being tested. Cursor and OpenAI Codex support are planned. Public availability is coming soon.",
  ],
  [
    "How does it relate to Halo SuperVPN?",
    "Independent products from the same LightningFlow family: Halo protects your connection, SecureShield protects your Mac, and SecureAI protects your AI connection.",
  ],
];

function SectionHeading({ eyebrow, title, body, light = false }) {
  return (
    <div className="max-w-3xl">
      <div className={`lf-eyebrow ${light ? "text-cyan-200" : ""}`}>{eyebrow}</div>
      <h2 className="mt-4 font-display text-4xl font-black leading-[1.02] md:text-5xl">{title}</h2>
      {body ? (
        <p className={`mt-5 text-lg leading-relaxed ${light ? "text-slate-200" : "text-ink-600"}`}>{body}</p>
      ) : null}
    </div>
  );
}

function TrustStrip() {
  const items = [
    ["Availability", SECUREAI_RELEASE.cta_label],
    ["Made for", "Apple Silicon Mac"],
    ["Your privacy", "No prompt, code or token capture"],
  ];

  return (
    <section aria-label="SecureAI release and privacy summary" className="border-y border-ink-100 bg-white">
      <div className="lf-container grid gap-px bg-ink-100 md:grid-cols-3">
        {items.map(([label, value]) => (
          <div key={label} className="bg-white px-6 py-6">
            <div className="font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{label}</div>
            <div className="mt-2 text-sm font-semibold leading-relaxed text-ink-900">{value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DownloadCard({ dark = false }) {
  const ready = downloadReady;
  const className = dark
    ? "border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
    : "border-cyan-300 bg-cyan-300 text-ink-900 hover:border-ink-900 hover:bg-white";

  if (!ready) {
    return (
      <div
        className={`h-full border px-5 py-6 text-left ${
          dark ? "border-white/20 bg-white/[.05] text-white" : "border-ink-100 bg-white text-ink-700"
        }`}
        aria-label="SecureAI macOS download coming soon"
        data-testid="secureai-download-card"
      >
        <span className="font-mono text-[9px] uppercase tracking-[.2em] opacity-70">macOS</span>
        <span className="mt-2 block font-display text-xl font-bold">{macosDownloadLabel()}</span>
        <span className="mt-2 block text-sm opacity-75">{macos.os} · {macos.chip}</span>
      </div>
    );
  }

  return (
    <a
      className={`block h-full border px-5 py-6 text-left transition-colors ${className}`}
      href="#download"
      data-testid="secureai-download-card"
    >
      <span className="font-mono text-[9px] uppercase tracking-[.2em] opacity-70">
        Developer ID · notarized
      </span>
      <span className="mt-2 flex items-center gap-2 font-display text-xl font-bold">
        <Download className="h-5 w-5" strokeWidth={1.8} />
        {macosDownloadLabel()}
      </span>
      <span className="mt-2 block text-sm opacity-75">
        {macos.os} · {macos.chip} · v{macos.version}
      </span>
    </a>
  );
}

function VerificationBlock({ light = false }) {
  if (!downloadReady) return null;

  return (
    <div
      className={`mt-8 border p-5 md:p-6 ${
        light ? "border-white/15 bg-white/[.04] text-slate-200" : "border-ink-100 bg-ink-50/50 text-ink-700"
      }`}
      data-testid="secureai-sha256-block"
    >
      <div className={`font-mono text-[9px] uppercase tracking-[.2em] ${light ? "text-cyan-200" : "text-ink-500"}`}>
        Verify your download
      </div>
      <p className={`mt-3 text-sm leading-relaxed ${light ? "text-slate-300" : "text-ink-600"}`}>
        Before you open the DMG, confirm the SHA-256 matches. Trust the digest, not the filename.
      </p>
      <dl className="mt-5 space-y-4 text-sm">
        <div>
          <dt className={`font-mono text-[10px] uppercase tracking-[.18em] ${light ? "text-cyan-200/80" : "text-ink-400"}`}>
            SHA-256
          </dt>
          <dd className="mt-2 break-all font-mono text-[12px] leading-relaxed tracking-tight">{macos.sha256}</dd>
        </div>
        <div>
          <dt className={`font-mono text-[10px] uppercase tracking-[.18em] ${light ? "text-cyan-200/80" : "text-ink-400"}`}>
            Signed by
          </dt>
          <dd className="mt-1">{macos.identity}</dd>
        </div>
        <div>
          <dt className={`font-mono text-[10px] uppercase tracking-[.18em] ${light ? "text-cyan-200/80" : "text-ink-400"}`}>
            Distribution
          </dt>
          <dd className="mt-1">{macos.distribution}. {macos.channel}.</dd>
        </div>
      </dl>
      <p className={`mt-5 text-xs leading-relaxed ${light ? "text-slate-400" : "text-ink-500"}`}>
        macOS only for this Apple launch. Windows is not claimed ready here.
      </p>
    </div>
  );
}

export default function SecureAIProductPage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "SecureAI",
    applicationCategory: "SecurityApplication",
    operatingSystem: "macOS",
    description: SECUREAI_CUSTOMER_PROMISE.description,
    // No public downloadUrl and no free InStock offer while the DMG is dark.
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/PreOrder",
      priceCurrency: "AUD",
      description: SECUREAI_RELEASE.summary,
    },
  };

  return (
    <div className="bg-white text-ink-900">
      <SEO
        title="SecureAI — Protect Your AI Connection."
        description={SECUREAI_CUSTOMER_PROMISE.description}
        canonicalPath="/secureai"
        image="/secureai/official-secureai-wordmark.png"
        structuredData={structuredData}
      />

      <section className="lf-product-hero relative overflow-hidden bg-[#031124] text-white">
        <div className="absolute inset-0 lf-grid-bg-faint opacity-20" />
        <div className="lf-container relative py-20 md:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_.85fr]">
            <div className="max-w-3xl">
              <div className="lf-eyebrow text-cyan-200">{SECUREAI_HERO_STRAPLINE}</div>
              <h1 className="mt-5 font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">
                {SECUREAI_CUSTOMER_PROMISE.headline}
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
                {SECUREAI_CUSTOMER_PROMISE.description}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:border-white hover:bg-white"
                  href="#download"
                  data-testid="secureai-cta-download"
                >
                  {SECUREAI_RELEASE.cta_label} <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  className="lf-btn border border-white/40 text-white hover:border-white"
                  href={downloadReady ? "#download" : "#platforms"}
                  data-testid="secureai-cta-verify"
                >
                  {downloadReady ? "Verify your download" : "Explore AI tools"}
                </a>
              </div>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400">
                Made for Apple Silicon Mac. Public downloads coming soon.
              </p>
            </div>
            <div className="relative mx-auto flex w-full max-w-md flex-col items-center gap-8 lg:mx-0 lg:justify-self-end" data-testid="secureai-brand-lockup">
              <img
                src="/secureai/official-secureai-hero-transparent-9cae3ec8.svg"
                alt=""
                className="h-auto w-full max-w-[22rem]"
                width={560}
                height={560}
              />
              <img
                src="/secureai/official-secureai-wordmark-transparent-788632a4.svg"
                alt="SecureAI by LightningFlow"
                className="h-auto w-full max-w-[20rem]"
                width={600}
                height={152}
              />
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />

      <section className="lf-editorial-section">
        <div className="lf-container grid gap-14 lg:grid-cols-[.68fr_1.32fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow="Why SecureAI"
              title="AI workflows deserve a protected path."
              body="Simple and clear on the surface. The same LightningFlow security discipline underneath."
            />
          </div>
          <div className="mt-10 divide-y divide-ink-200 border-y border-ink-200">
            {benefits.map(({ icon: Icon, eyebrow, title, body }, index) => (
              <SectionReveal as="article" key={title} className="lf-feature-panel grid grid-cols-[2rem_1fr] gap-4 py-6">
                <span className="font-mono text-[10px] tracking-[.18em] text-electric">0{index + 1}</span>
                <div>
                  <Icon className="h-5 w-5 text-electric" strokeWidth={1.6} />
                  <div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-ink-500">{eyebrow}</div>
                  <h3 className="mt-2 font-display text-2xl font-bold">{title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-600">{body}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="capabilities" className="scroll-mt-24 border-y border-ink-100 bg-[#031124] text-white">
        <div className="lf-container py-20 md:py-28">
          <SectionHeading
            light
            eyebrow="Designed for your workflow"
            title="Protection you can understand."
            body="Keep compatible AI connections private, with a clear view of their protection."
          />
          <div className="mt-10 grid gap-px bg-white/10 md:grid-cols-2">
            {SECUREAI_CAPABILITIES.map((item, index) => {
              const Icon = capabilityIcons[index] || ShieldCheck;
              return (
                <SectionReveal as="article" key={item.title} className="lf-feature-panel bg-[#031124] p-7 md:p-9">
                  <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.6} />
                  <div className="mt-5 font-mono text-[9px] uppercase tracking-[.2em] text-cyan-200">
                    {item.eyebrow}
                  </div>
                  <h3 className="mt-2 font-display text-2xl font-bold">{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-slate-300">{item.body}</p>
                </SectionReveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="platforms" className="lf-section scroll-mt-24 border-y border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <SectionHeading
            eyebrow="AI tools"
            title="See what’s coming."
            body="Tool names identify compatibility targets. They do not imply partnership or endorsement."
          />
          <div className="mt-10 divide-y divide-ink-200 border border-ink-100 bg-white">
            {SECUREAI_PLATFORMS.map((platform) => (
              <article
                key={platform.name}
                className="grid gap-4 p-6 md:grid-cols-[11rem_8rem_1fr] md:items-start"
                data-testid={`secureai-platform-${platform.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <h3 className="font-display text-xl font-bold">{platform.name}</h3>
                <div className="font-mono text-[10px] uppercase tracking-[.18em] text-electric">
                  {platform.status}
                </div>
                <p className="leading-relaxed text-ink-600">{platform.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="download" className="scroll-mt-24 bg-[#031124] text-white">
        <div className="lf-container py-16 md:py-20">
          <SectionHeading
            eyebrow="Download for Mac"
            title={downloadReady ? "Get SecureAI for your Mac." : "Coming soon to your Mac."}
            body={SECUREAI_RELEASE.summary}
            light
          />
          <div className="mt-10 grid gap-3 sm:max-w-md">
            <DownloadCard dark />
          </div>
          <VerificationBlock light />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              className="lf-btn border border-white/40 text-white hover:border-white"
              to="/halo"
              data-testid="secureai-cta-halo-secondary"
            >
              Discover Halo SuperVPN
            </Link>
            <Link
              className="lf-btn border border-white/40 text-white hover:border-white"
              to="/support"
              data-testid="secureai-cta-support"
            >
              Contact LightningFlow Support <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section id="privacy" className="border-y border-ink-100 bg-ink-900 text-white">
        <div className="lf-container grid gap-12 py-16 md:py-20 lg:grid-cols-[.72fr_1.28fr]">
          <div>
            <LockKeyhole className="h-7 w-7 text-cyan-300" />
            <h2 className="mt-5 font-display text-4xl font-black">Private by design.</h2>
            <p className="mt-5 leading-relaxed text-slate-200">
              SecureAI keeps only what operation needs. It does not retain prompts, source code or token content as activity records.
            </p>
          </div>
          <div className="grid gap-px bg-white/10 sm:grid-cols-2">
            <article className="bg-ink-900 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">Never collected</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li>Prompt or chat content</li>
                <li>Source code payloads</li>
                <li>API tokens or secrets</li>
                <li>Browsing-style activity logs</li>
              </ul>
            </article>
            <article className="bg-ink-900 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">Kept for operation</div>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li>Account and connection status</li>
                <li>Connection health signals</li>
                <li>Security and service diagnostics</li>
                <li>Account information when licensed</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="lf-section border-b border-ink-100 bg-ink-50/60">
        <div className="lf-container">
          <SectionHeading
            eyebrow="From the same team"
            title="Protect your connection. Protect your Mac. Protect your AI connection."
            body="Independent LightningFlow consumer products. Same calm, honest standard."
          />
          <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-3">
            {SECUREAI_FAMILY.map((item) => (
              <article key={item.slug} className="bg-white p-7 md:p-9">
                <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">
                  {item.tagline}
                </div>
                <h3 className="mt-5 font-display text-3xl font-black">{item.label}</h3>
                <p className="mt-4 leading-relaxed text-ink-600">{item.body}</p>
                {item.slug === "secureai" ? (
                  <span className="mt-7 inline-flex font-mono text-[10px] uppercase tracking-[.18em] text-electric">
                    You are here
                  </span>
                ) : (
                  <Link
                    className="lf-btn-secondary mt-7"
                    to={`/${item.slug}`}
                    data-testid={`secureai-related-${item.slug}`}
                  >
                    Discover {item.label} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-section">
        <div className="lf-container grid gap-12 lg:grid-cols-[.65fr_1.35fr]">
          <SectionHeading eyebrow="Questions" title="Clear before you download." />
          <div className="divide-y divide-ink-100 border border-ink-100 bg-white">
            {faq.map(([question, answer]) => (
              <details key={question} className="group p-6">
                <summary className="cursor-pointer list-none pr-8 font-display text-lg font-bold marker:hidden">
                  {question}
                </summary>
                <p className="mt-4 max-w-3xl leading-relaxed text-ink-600">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lf-editorial-section lf-editorial-dark" data-testid="secureai-final-cta">
        <div className="lf-container grid gap-10 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div>
            <p className="lf-eyebrow text-cyan-200">SecureAI</p>
            <h2 className="mt-5 max-w-5xl font-display text-5xl font-black leading-[.92] tracking-[-.04em] md:text-7xl">
              Private AI protection should feel this simple.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              A private path for supported AI tools. A clear view of protection. Your work stays yours.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <a
              className="lf-btn border border-cyan-300 bg-cyan-300 text-ink-900 hover:bg-white"
              href="#download"
              data-testid="secureai-cta-download-final"
            >
              {SECUREAI_RELEASE.cta_label} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
